import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Star,
  Check,
  Crown,
  Shield,
  Award,
  ArrowLeft,
  Sparkles,
  RefreshCw,
} from "lucide-react";
import subscriptionApi from "../api/subscriptionApi";
import { toast } from "react-toastify";
import { RAZORPAY_CHECKOUT_CONFIG } from "../config/razorpay.config";

const MembershipPlansPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentPlan: locationCurrentPlan = "Basic", userId: locationUserId } =
    location.state || {};

  // Get userId from location state or localStorage
  const [userId, setUserId] = useState(locationUserId);
  const [currentPlan, setCurrentPlan] = useState(locationCurrentPlan);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);
  const [planPrices, setPlanPrices] = useState({ SILVER: 2999, GOLD: 5999, PLATINUM: 9999 });

  // Get userId from localStorage if not in location state
  useEffect(() => {
    if (!userId) {
      const storedUserId = localStorage.getItem("userId");
      if (storedUserId) {
        setUserId(storedUserId);
      } else {
        console.error("No userId found in location state or localStorage");
      }
    }
  }, [userId]);

  // Fetch plan prices from backend
  useEffect(() => {
    const fetchPlanPrices = async () => {
      try {
        const response = await subscriptionApi.getPlanPrices();
        if (response.success && response.data) {
          setPlanPrices(response.data);
        }
      } catch (error) {
        console.error("Error fetching plan prices:", error);
      }
    };
    fetchPlanPrices();
  }, []);

  // Fetch current subscription status
  useEffect(() => {
    const fetchSubscription = async () => {
      if (userId) {
        try {
          const response = await subscriptionApi.getCurrentPlan(userId);

          if (response.success && response.data) {
            const planType =
              response.data.membershipTier || response.data.planType || "Basic";
            setCurrentPlan(planType);
          } else {
            console.error("API call failed:", response.error);
          }
        } catch (error) {
          console.error("Error fetching subscription:", error);
        }
      }
    };
    fetchSubscription();
  }, [userId]);

  const plans = [
    {
      id: "basic",
      name: "Basic",
      price: 0,
      period: "Forever",
      color: "gray",
      icon: Shield,
      gradient: "from-gray-400 to-gray-500",
      bgGradient: "from-gray-50/90 to-gray-100/70",
      borderColor: "border-gray-200/60",
      description: "Essential features for casual travelers",
      features: [
        "Basic booking access",
        "Email notifications",
        "Standard customer support",
        "Basic profile management",
      ],
      limitations: [
        "No discounts",
        "No priority booking",
        "Limited support hours",
      ],
    },
    {
      id: "silver",
      name: "Silver",
      price: planPrices.SILVER || 2999,
      period: "per year",
      color: "blue",
      icon: Star,
      popular: true,
      gradient: "from-blue-500 to-indigo-600",
      bgGradient: "from-blue-50/90 to-indigo-100/70",
      borderColor: "border-blue-300/60",
      description: "Perfect for frequent travelers",
      features: [
        "10% discount on room bookings",
        "5% discount on restaurant bookings",
        "Priority customer support",
        "Early check-in (subject to availability)",
        "Loyalty points on every booking",
        "Free WiFi upgrade",
        "Monthly exclusive offers",
      ],
    },
    {
      id: "gold",
      name: "Gold",
      price: planPrices.GOLD || 5999,
      period: "per year",
      color: "yellow",
      icon: Award,
      gradient: "from-yellow-500 to-orange-600",
      bgGradient: "from-yellow-50/90 to-orange-100/70",
      borderColor: "border-yellow-300/60",
      description: "Premium experience for luxury seekers",
      features: [
        "20% discount on room bookings",
        "15% discount on restaurant bookings",
        "Priority booking access",
        "Free room upgrades (subject to availability)",
        "Complimentary breakfast",
        "Late checkout until 2 PM",
        "24/7 concierge service",
        "Double loyalty points",
        "Welcome drink on arrival",
      ],
    },
    {
      id: "platinum",
      name: "Platinum",
      price: planPrices.PLATINUM || 9999,
      period: "per year",
      color: "purple",
      icon: Crown,
      premium: true,
      gradient: "from-purple-500 to-pink-600",
      bgGradient: "from-purple-50/90 to-pink-100/70",
      borderColor: "border-purple-300/60",
      description: "Ultimate luxury and personalized service",
      features: [
        "30% discount on room bookings",
        "25% discount on restaurant bookings",
        "Guaranteed room upgrades",
        "Complimentary breakfast + dinner",
        "Free airport pickup/drop (within city)",
        "Personal butler service",
        "Triple loyalty points",
        "Access to exclusive lounges",
        "Priority table reservations",
        "Spa and wellness discounts",
        "Special occasion surprises",
      ],
    },
  ];

  const handlePlanSelect = async (plan) => {
    if (plan.id === "basic") return;

    setLoading(true);
    setSelectedPlan(plan.id);
    setError(null);
    setPaymentStatus(null);

    try {
      if (!userId) {
        const errorMsg = "User not logged in. Please log in and try again.";
        throw new Error(errorMsg);
      }

      if (!window.Razorpay) {
        throw new Error(
          "Payment system not loaded. Please refresh the page and try again."
        );
      }

      setPaymentStatus("processing");
      const orderResponse = await subscriptionApi.createPaymentOrder(
        userId,
        plan.name,
        plan.price
      );

      if (!orderResponse.success) {
        throw new Error(
          orderResponse.error || "Failed to create payment order"
        );
      }

      const { orderId, amount, currency, razorpayKey } = orderResponse.data;

      const options = {
        key: razorpayKey,
        amount: amount,
        currency: currency,
        name: "Trivana Hospitality",
        description: `Membership Plan Upgrade - ${plan.name}`,
        order_id: orderId,
        method: {
          upi: true,
          card: true,
          netbanking: true,
          wallet: true,
        },
        handler: async (paymentResponse) => {
          try {
            const upgradeData = {
              planType: plan.name.toUpperCase(),
              paymentMethod: "RAZORPAY",
              transactionId: paymentResponse.razorpay_payment_id,
              amount: plan.price,
              currency: "INR",
              razorpayOrderId: paymentResponse.razorpay_order_id,
              razorpaySignature: paymentResponse.razorpay_signature,
            };

            const upgradeResult = await subscriptionApi.upgradePlan(
              userId,
              upgradeData
            );

            if (upgradeResult.success) {
              setPaymentStatus("success");
              // Update current plan immediately
              setCurrentPlan(plan.name.toUpperCase());
              toast.success("🎉 Membership upgraded successfully!");

              // Dispatch membership change event for real-time UI updates
              window.dispatchEvent(
                new CustomEvent("membershipChanged", {
                  detail: {
                    newPlan: plan.name.toUpperCase(),
                    userId: userId,
                  },
                })
              );

              // Force refresh the subscription data immediately with retry logic
              setTimeout(async () => {
                try {
                  const freshData = await subscriptionApi.getCurrentPlan(
                    userId
                  );

                  if (freshData.success && freshData.data) {
                    const newPlan =
                      freshData.data.membershipTier || freshData.data.planType;
                    setCurrentPlan(newPlan);

                    // If still showing BASIC, try again after 1 second
                    if (newPlan === "BASIC") {
                      setTimeout(async () => {
                        const retryData = await subscriptionApi.getCurrentPlan(
                          userId
                        );
                        if (retryData.success && retryData.data) {
                          const retryPlan =
                            retryData.data.membershipTier ||
                            retryData.data.planType;
                          setCurrentPlan(retryPlan);
                        }
                      }, 1000);
                    }
                  }
                } catch (err) {
                  console.error("Error fetching fresh data:", err);
                }
              }, 500);

              setTimeout(() => {
                navigate("/profile", {
                  state: {
                    activeTab: "subscription",
                    refreshSubscription: true,
                  },
                });
              }, 2000); // Increased to 2 seconds to allow retry
            } else {
              console.error("Upgrade failed with error:", upgradeResult.error);
              throw new Error(upgradeResult.error || "Plan upgrade failed");
            }
          } catch (error) {
            console.error("Payment verification failed:", error);
            toast.error("Payment verification failed. Please contact support.");
            setPaymentStatus("failed");
          }
        },
        prefill: (() => {
          try {
            const u = JSON.parse(localStorage.getItem("trivana_user") || "{}");
            return {
              name:    u.name    || u.fullName || "",
              email:   u.email   || "",
              contact: u.phone   || u.mobile   || "",
            };
          } catch { return { name: "", email: "", contact: "" }; }
        })(),
        notes: {
          plan_name: plan.name,
          user_id: userId,
        },
        theme: {
          color: "#EE2E24",
        },
        modal: {
          ondismiss: function () {
            setPaymentStatus("cancelled");
            setTimeout(() => {
              setPaymentStatus(null);
            }, 2000);
          },
        },
        ...RAZORPAY_CHECKOUT_CONFIG,
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error("Error in handlePlanSelect:", error);
      setError(error.message);
      setPaymentStatus("failed");
      toast.error(error.message);
      setTimeout(() => {
        setPaymentStatus(null);
        setError(null);
      }, 3000);
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const getButtonText = (plan) => {
    const normalizedCurrentPlan = currentPlan.toLowerCase().trim();
    const isCurrentPlan =
      normalizedCurrentPlan === plan.id ||
      normalizedCurrentPlan === plan.name.toLowerCase() ||
      (normalizedCurrentPlan === "basic" && plan.id === "basic");
    const isSelected = plan.id === selectedPlan;

    if (isCurrentPlan) return "Current Plan";
    if (plan.id === "basic") return "Free Plan";
    if (loading && isSelected) {
      if (paymentStatus === "processing") return "Processing Payment...";
      if (paymentStatus === "success") return "Activated! ✓";
      if (paymentStatus === "failed") return "Payment Failed ✗";
      return "Processing...";
    }
    return `Upgrade to ${plan.name}`;
  };

  const isCurrentPlan = (plan) => {
    const normalizedCurrentPlan = currentPlan.toLowerCase().trim();
    return (
      normalizedCurrentPlan === plan.id ||
      normalizedCurrentPlan === plan.name.toLowerCase() ||
      (normalizedCurrentPlan === "basic" && plan.id === "basic")
    );
  };

  const handleRefreshStatus = async () => {
    if (!userId) {
      toast.error("User ID not found");
      return;
    }

    try {
      const response = await subscriptionApi.getCurrentPlan(userId);

      if (response.success && response.data) {
        const newPlan = response.data.membershipTier || response.data.planType;
        setCurrentPlan(newPlan);
        toast.success(`Status refreshed! Current plan: ${newPlan}`);
      } else {
        toast.error("Failed to refresh status");
      }
    } catch (error) {
      console.error("Error refreshing status:", error);
      toast.error("Error refreshing status");
    }
  };

  return (
    <div
      style={{
        paddingTop: "75px",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #FFF5F5 0%, #FFF9E6 100%)",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        paddingBottom: "30px",
      }}
    >
      <div style={{ maxWidth: "1320px", margin: "0 auto", padding: "0 16px" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "24px",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <button
            onClick={() => navigate(-1)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "8px 16px",
              background: "rgba(238, 46, 36, 0.08)",
              border: "1px solid rgba(238, 46, 36, 0.15)",
              borderRadius: "10px",
              color: "#EE2E24",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.25s ease",
              letterSpacing: "-0.3px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(238, 46, 36, 0.12)";
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow =
                "0 3px 8px rgba(238, 46, 36, 0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(238, 46, 36, 0.08)";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
          <div style={{ flex: 1, textAlign: "center" }}>
            <h1
              style={{
                margin: 0,
                fontSize: "clamp(1.5rem, 3.5vw, 2rem)",
                fontWeight: "800",
                background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                letterSpacing: "-0.8px",
                paddingTop: "15px",
              }}
            >
              Membership Plans
            </h1>
            <p
              style={{
                marginTop: "4px",
                fontSize: "clamp(0.75rem, 1.8vw, 0.875rem)",
                color: "#64748B",
                fontWeight: "500",
                letterSpacing: "-0.3px",
              }}
            >
              Unlock exclusive benefits & save more
            </p>
          </div>
          <button
            onClick={handleRefreshStatus}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "8px 16px",
              background: "rgba(34, 197, 94, 0.08)",
              border: "1px solid rgba(34, 197, 94, 0.15)",
              borderRadius: "10px",
              color: "#16A34A",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.25s ease",
              letterSpacing: "-0.3px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(34, 197, 94, 0.12)";
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow =
                "0 3px 8px rgba(34, 197, 94, 0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(34, 197, 94, 0.08)";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {/* Status Messages */}
        {error && (
          <div
            style={{
              marginBottom: "20px",
              padding: "12px 16px",
              background: "linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)",
              border: "1px solid rgba(239, 68, 68, 0.25)",
              borderRadius: "10px",
              boxShadow: "0 3px 8px rgba(239, 68, 68, 0.08)",
            }}
          >
            <p
              style={{
                color: "#991B1B",
                fontSize: "13px",
                fontWeight: "600",
                margin: 0,
                letterSpacing: "-0.3px",
              }}
            >
              ⚠️ {error}
            </p>
          </div>
        )}

        {paymentStatus === "success" && (
          <div
            style={{
              marginBottom: "20px",
              padding: "12px 16px",
              background: "linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)",
              border: "1px solid rgba(34, 197, 94, 0.25)",
              borderRadius: "10px",
              boxShadow: "0 3px 8px rgba(34, 197, 94, 0.08)",
            }}
          >
            <p
              style={{
                color: "#065F46",
                fontSize: "13px",
                fontWeight: "600",
                margin: 0,
                letterSpacing: "-0.3px",
              }}
            >
              🎉 Payment successful! Your plan has been upgraded.
            </p>
          </div>
        )}

        {/* Plans Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "20px",
            marginBottom: "30px",
            paddingTop: "16px",
          }}
        >
          {plans.map((plan) => {
            const Icon = plan.icon;
            const isCurrent = isCurrentPlan(plan);
            const isSelected = selectedPlan === plan.id;

            return (
              <div
                key={plan.id}
                style={{
                  position: "relative",
                  marginTop: plan.popular || plan.premium ? "12px" : "0",
                  background: `linear-gradient(135deg, ${
                    plan.popular
                      ? "rgba(219, 234, 254, 0.6), rgba(191, 219, 254, 0.5)"
                      : plan.premium
                      ? "rgba(243, 232, 255, 0.6), rgba(233, 213, 255, 0.5)"
                      : isCurrent
                      ? "rgba(220, 252, 231, 0.6), rgba(187, 247, 208, 0.5)"
                      : "rgba(255, 255, 255, 0.8), rgba(248, 250, 252, 0.7)"
                  }`,
                  backdropFilter: "blur(12px)",
                  borderRadius: "16px",
                  border: `1.5px solid ${
                    plan.popular
                      ? "rgba(59, 130, 246, 0.25)"
                      : plan.premium
                      ? "rgba(168, 85, 247, 0.25)"
                      : isCurrent
                      ? "rgba(34, 197, 94, 0.25)"
                      : "rgba(238, 46, 36, 0.12)"
                  }`,
                  boxShadow:
                    plan.popular || plan.premium
                      ? "0 8px 24px rgba(238, 46, 36, 0.1)"
                      : "0 4px 16px rgba(0, 0, 0, 0.06)",
                  overflow: "visible",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  transform: plan.popular ? "scale(1.03)" : "scale(1)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = plan.popular
                    ? "scale(1.05)"
                    : "scale(1.02)";
                  e.currentTarget.style.boxShadow =
                    "0 12px 32px rgba(238, 46, 36, 0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = plan.popular
                    ? "scale(1.03)"
                    : "scale(1)";
                  e.currentTarget.style.boxShadow =
                    plan.popular || plan.premium
                      ? "0 8px 24px rgba(238, 46, 36, 0.1)"
                      : "0 4px 16px rgba(0, 0, 0, 0.06)";
                }}
              >
                {/* Badges */}
                {plan.popular && (
                  <div
                    style={{
                      position: "absolute",
                      top: "-12px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      zIndex: 10,
                    }}
                  >
                    <span
                      style={{
                        background:
                          "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                        color: "white",
                        padding: "5px 14px",
                        borderRadius: "16px",
                        fontSize: "10.5px",
                        fontWeight: "700",
                        whiteSpace: "nowrap",
                        boxShadow: "0 3px 8px rgba(238, 46, 36, 0.25)",
                        letterSpacing: "-0.2px",
                        display: "inline-block",
                      }}
                    >
                      ⭐ Most Popular
                    </span>
                  </div>
                )}

                {plan.premium && (
                  <div
                    style={{
                      position: "absolute",
                      top: "-12px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      zIndex: 10,
                    }}
                  >
                    <span
                      style={{
                        background:
                          "linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)",
                        color: "white",
                        padding: "5px 14px",
                        borderRadius: "16px",
                        fontSize: "10.5px",
                        fontWeight: "700",
                        whiteSpace: "nowrap",
                        boxShadow: "0 3px 8px rgba(139, 92, 246, 0.25)",
                        letterSpacing: "-0.2px",
                        display: "inline-block",
                      }}
                    >
                      👑 Premium
                    </span>
                  </div>
                )}

                {isCurrent && (
                  <div
                    style={{
                      position: "absolute",
                      top: "12px",
                      right: "12px",
                      zIndex: 10,
                    }}
                  >
                    <span
                      style={{
                        background:
                          "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                        color: "white",
                        padding: "3px 10px",
                        borderRadius: "16px",
                        fontSize: "10px",
                        fontWeight: "700",
                        boxShadow: "0 2px 6px rgba(16, 185, 129, 0.25)",
                        letterSpacing: "-0.2px",
                      }}
                    >
                      ✓ Active
                    </span>
                  </div>
                )}

                {/* Card Content */}
                <div style={{ padding: "20px 16px" }}>
                  {/* Icon */}
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "48px",
                      height: "48px",
                      borderRadius: "50%",
                      background: `linear-gradient(135deg, ${
                        plan.color === "blue"
                          ? "#3B82F6, #2563EB"
                          : plan.color === "purple"
                          ? "#8B5CF6, #7C3AED"
                          : plan.color === "yellow"
                          ? "#F59E0B, #D97706"
                          : "#6B7280, #4B5563"
                      })`,
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
                      marginBottom: "12px",
                    }}
                  >
                    <Icon size={24} color="white" />
                  </div>

                  {/* Name */}
                  <h3
                    style={{
                      fontSize: "20px",
                      fontWeight: "800",
                      color: "#1F2937",
                      marginBottom: "8px",
                      letterSpacing: "-0.6px",
                    }}
                  >
                    {plan.name}
                  </h3>

                  {/* Price */}
                  <div style={{ marginBottom: "10px" }}>
                    <span
                      style={{
                        fontSize: "34px",
                        fontWeight: "800",
                        background:
                          "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        letterSpacing: "-1.2px",
                      }}
                    >
                      ₹{plan.price.toLocaleString()}
                    </span>
                    {plan.period !== "Forever" && (
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#64748B",
                          fontWeight: "600",
                          marginLeft: "3px",
                          letterSpacing: "-0.3px",
                        }}
                      >
                        /{plan.period}
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  <p
                    style={{
                      color: "#475569",
                      fontSize: "12px",
                      lineHeight: "1.5",
                      marginBottom: "16px",
                      fontWeight: "500",
                      letterSpacing: "-0.3px",
                    }}
                  >
                    {plan.description}
                  </p>

                  {/* Features */}
                  <div style={{ marginBottom: "16px" }}>
                    {plan.features.map((feature, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "8px",
                          padding: "7px 10px",
                          background: "rgba(255, 255, 255, 0.6)",
                          borderRadius: "8px",
                          border: "1px solid rgba(238, 46, 36, 0.08)",
                          marginBottom: "6px",
                          transition: "all 0.25s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            "rgba(255, 255, 255, 0.85)";
                          e.currentTarget.style.transform = "translateX(3px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background =
                            "rgba(255, 255, 255, 0.6)";
                          e.currentTarget.style.transform = "translateX(0)";
                        }}
                      >
                        <div
                          style={{
                            width: "16px",
                            height: "16px",
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            marginTop: "1px",
                          }}
                        >
                          <Check size={10} color="white" strokeWidth={3} />
                        </div>
                        <span
                          style={{
                            color: "#1F2937",
                            fontSize: "11.5px",
                            lineHeight: "1.4",
                            fontWeight: "500",
                            letterSpacing: "-0.2px",
                          }}
                        >
                          {feature}
                        </span>
                      </div>
                    ))}

                    {plan.limitations && (
                      <div
                        style={{
                          marginTop: "12px",
                          paddingTop: "12px",
                          borderTop: "1px solid rgba(238, 46, 36, 0.08)",
                        }}
                      >
                        {plan.limitations.map((limitation, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "8px",
                              padding: "6px 10px",
                              background: "rgba(254, 226, 226, 0.4)",
                              borderRadius: "6px",
                              marginBottom: "5px",
                              opacity: 0.7,
                            }}
                          >
                            <div
                              style={{
                                width: "14px",
                                height: "14px",
                                borderRadius: "50%",
                                background:
                                  "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                marginTop: "1px",
                              }}
                            >
                              <span
                                style={{
                                  color: "white",
                                  fontSize: "9px",
                                  fontWeight: "700",
                                }}
                              >
                                ✕
                              </span>
                            </div>
                            <span
                              style={{
                                color: "#991B1B",
                                fontSize: "11px",
                                lineHeight: "1.3",
                                fontWeight: "500",
                                letterSpacing: "-0.2px",
                              }}
                            >
                              {limitation}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Button */}
                  <button
                    onClick={() => handlePlanSelect(plan)}
                    disabled={isCurrent || (loading && isSelected)}
                    style={{
                      width: "100%",
                      padding: "11px 16px",
                      borderRadius: "10px",
                      fontWeight: "700",
                      fontSize: "13px",
                      border: "none",
                      cursor:
                        isCurrent || (loading && isSelected)
                          ? "not-allowed"
                          : "pointer",
                      background: isCurrent
                        ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
                        : plan.id === "basic"
                        ? "linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)"
                        : loading && isSelected
                        ? "linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)"
                        : "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                      color: "white",
                      boxShadow: "0 3px 8px rgba(238, 46, 36, 0.15)",
                      transition: "all 0.25s ease",
                      opacity: isCurrent || (loading && isSelected) ? 0.7 : 1,
                      letterSpacing: "-0.3px",
                    }}
                    onMouseEnter={(e) => {
                      if (
                        !isCurrent &&
                        plan.id !== "basic" &&
                        !(loading && isSelected)
                      ) {
                        e.currentTarget.style.transform = "translateY(-1px)";
                        e.currentTarget.style.boxShadow =
                          "0 6px 16px rgba(238, 46, 36, 0.25)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 3px 8px rgba(238, 46, 36, 0.15)";
                    }}
                  >
                    {getButtonText(plan)}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Benefits Section */}
        <div
          style={{
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(248, 250, 252, 0.7) 100%)",
            backdropFilter: "blur(12px)",
            borderRadius: "16px",
            border: "1px solid rgba(238, 46, 36, 0.08)",
            padding: "24px",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.06)",
          }}
        >
          <h3
            style={{
              fontSize: "clamp(1.25rem, 2.5vw, 1.5rem)",
              fontWeight: "800",
              background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              textAlign: "center",
              marginBottom: "24px",
              letterSpacing: "-0.6px",
            }}
          >
            <Sparkles
              size={22}
              style={{
                display: "inline",
                marginRight: "8px",
                color: "#FFC400",
              }}
            />
            Why Premium Membership?
          </h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "20px",
            }}
          >
            <div
              style={{
                textAlign: "center",
                padding: "20px 16px",
                background: "rgba(255, 255, 255, 0.6)",
                borderRadius: "12px",
                border: "1px solid rgba(59, 130, 246, 0.15)",
                transition: "all 0.25s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-6px)";
                e.currentTarget.style.boxShadow =
                  "0 8px 20px rgba(59, 130, 246, 0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                  boxShadow: "0 4px 12px rgba(59, 130, 246, 0.25)",
                }}
              >
                <Star size={22} color="white" />
              </div>
              <h4
                style={{
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "#1F2937",
                  marginBottom: "8px",
                  letterSpacing: "-0.4px",
                }}
              >
                💰 Save More
              </h4>
              <p
                style={{
                  fontSize: "12px",
                  color: "#64748B",
                  lineHeight: "1.5",
                  fontWeight: "500",
                  letterSpacing: "-0.3px",
                }}
              >
                Get significant discounts on all bookings
              </p>
            </div>

            <div
              style={{
                textAlign: "center",
                padding: "20px 16px",
                background: "rgba(255, 255, 255, 0.6)",
                borderRadius: "12px",
                border: "1px solid rgba(139, 92, 246, 0.15)",
                transition: "all 0.25s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-6px)";
                e.currentTarget.style.boxShadow =
                  "0 8px 20px rgba(139, 92, 246, 0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                  boxShadow: "0 4px 12px rgba(139, 92, 246, 0.25)",
                }}
              >
                <Crown size={22} color="white" />
              </div>
              <h4
                style={{
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "#1F2937",
                  marginBottom: "8px",
                  letterSpacing: "-0.4px",
                }}
              >
                👑 Premium Experience
              </h4>
              <p
                style={{
                  fontSize: "12px",
                  color: "#64748B",
                  lineHeight: "1.5",
                  fontWeight: "500",
                  letterSpacing: "-0.3px",
                }}
              >
                Priority access, upgrades & services
              </p>
            </div>

            <div
              style={{
                textAlign: "center",
                padding: "20px 16px",
                background: "rgba(255, 255, 255, 0.6)",
                borderRadius: "12px",
                border: "1px solid rgba(236, 72, 153, 0.15)",
                transition: "all 0.25s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-6px)";
                e.currentTarget.style.boxShadow =
                  "0 8px 20px rgba(236, 72, 153, 0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #EC4899 0%, #DB2777 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                  boxShadow: "0 4px 12px rgba(236, 72, 153, 0.25)",
                }}
              >
                <Award size={22} color="white" />
              </div>
              <h4
                style={{
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "#1F2937",
                  marginBottom: "8px",
                  letterSpacing: "-0.4px",
                }}
              >
                🎁 Earn Rewards
              </h4>
              <p
                style={{
                  fontSize: "12px",
                  color: "#64748B",
                  lineHeight: "1.5",
                  fontWeight: "500",
                  letterSpacing: "-0.3px",
                }}
              >
                Accumulate loyalty points faster
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipPlansPage;
